import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  sidePanelExpanded: false,
  windowWidth: window.innerWidth,
  isFullscreen: false,
  // History tracking for PegaWebEmbed props
  embedPropsHistory: [], // Array of { props, timestamp }
  currentHistoryIndex: -1, // -1 means no history yet
  // Address bar state
  currentAddress: 'Home', // Current address bar value (pyID, 'Home', or case ID)
  // Pega access group info (cached for case ID resolution)
  accessGroupInfo: null,
  operatorInfo: null,
  caseResolverInitialized: false,
};

export const sidePanelSlice = createSlice({
  name: 'sidePanel',
  initialState,
  reducers: {
    toggleSidePanel: (state) => {
      state.sidePanelExpanded = !state.sidePanelExpanded;
    },
    setWindowWidth: (state, action) => {
      state.windowWidth = action.payload;
    },
    setSidePanelExpanded: (state, action) => {
      state.sidePanelExpanded = action.payload;
    },
    toggleFullscreen: (state) => {
      state.isFullscreen = !state.isFullscreen;
    },
    closeSidePanel: (state) => {
      state.sidePanelExpanded = false;
      state.isFullscreen = false;
    },
    minimizeSidePanel: (state) => {
      state.sidePanelExpanded = false;
    },
    // Add new embed props to history
    addToEmbedHistory: (state, action) => {
      const { props, title } = action.payload;
      const newProps = props || action.payload; // Support both formats
      const newTitle = title || null;
      
      // Don't add duplicate of current props
      const currentEntry = state.embedPropsHistory[state.currentHistoryIndex];
      if (currentEntry && JSON.stringify(currentEntry.props) === JSON.stringify(newProps)) {
        console.log('⏭️ Skipping duplicate embed history entry');
        return;
      }
      
      // If we're not at the end of history (user went back), remove forward history
      if (state.currentHistoryIndex < state.embedPropsHistory.length - 1) {
        state.embedPropsHistory = state.embedPropsHistory.slice(0, state.currentHistoryIndex + 1);
      }
      
      // Add new entry with both props and title
      state.embedPropsHistory.push({
        props: newProps,
        title: newTitle,
        timestamp: Date.now(),
      });
      
      // Keep only last 20 entries to prevent memory bloat
      if (state.embedPropsHistory.length > 20) {
        state.embedPropsHistory.shift();
      } else {
        state.currentHistoryIndex++;
      }
      
      console.log('📚 Added to embed history. Total entries:', state.embedPropsHistory.length, 'Current index:', state.currentHistoryIndex, 'Title:', newTitle);
    },
    // Navigate back in history
    navigateEmbedHistoryBack: (state) => {
      if (state.currentHistoryIndex > 0) {
        state.currentHistoryIndex--;
        console.log('⬅️ Navigated back to index:', state.currentHistoryIndex);
      } else {
        console.log('⚠️ Already at start of history');
      }
    },
    // Navigate forward in history
    navigateEmbedHistoryForward: (state) => {
      if (state.currentHistoryIndex < state.embedPropsHistory.length - 1) {
        state.currentHistoryIndex++;
        console.log('➡️ Navigated forward to index:', state.currentHistoryIndex);
      } else {
        console.log('⚠️ Already at end of history');
      }
    },
    // Clear history (called on reset or new config)
    clearEmbedHistory: (state) => {
      console.log('🗑️ Clearing embed history');
      state.embedPropsHistory = [];
      state.currentHistoryIndex = -1;
    },
    // Set current address bar value
    setCurrentAddress: (state, action) => {
      state.currentAddress = action.payload || 'Home';
      console.log('📍 Address bar updated:', state.currentAddress);
    },
    // Store access group info for case resolution
    setAccessGroupInfo: (state, action) => {
      state.accessGroupInfo = action.payload;
      console.log('🔐 Access group info stored:', action.payload?.pyDefaultWorkPool);
    },
    // Store operator info
    setOperatorInfo: (state, action) => {
      state.operatorInfo = action.payload;
      console.log('👤 Operator info stored:', action.payload?.pyUserIdentifier);
    },
    // Set case resolver initialized state
    setCaseResolverInitialized: (state, action) => {
      state.caseResolverInitialized = action.payload;
      console.log('🔧 Case resolver initialized:', action.payload);
    },
    // Clear access group info (called on story change or logout)
    clearAccessGroupInfo: (state) => {
      console.log('🗑️ Clearing access group info');
      state.accessGroupInfo = null;
      state.operatorInfo = null;
      state.caseResolverInitialized = false;
    },
  },
});

export const { 
  toggleSidePanel, 
  setWindowWidth, 
  setSidePanelExpanded, 
  toggleFullscreen, 
  closeSidePanel, 
  minimizeSidePanel,
  addToEmbedHistory,
  navigateEmbedHistoryBack,
  navigateEmbedHistoryForward,
  clearEmbedHistory,
  setCurrentAddress,
  setAccessGroupInfo,
  setOperatorInfo,
  setCaseResolverInitialized,
  clearAccessGroupInfo,
} = sidePanelSlice.actions;

export default sidePanelSlice.reducer;
